import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner } from "../../components/ui";
import { parseDate } from "./recordDerive";
import { RecordModal } from "./Inventory";

/** Standalone list of every approved record — full snapshot, who approved it, when, and how many processes it
 *  moved through to get approved. Fed by the approved_record table (also still visible in Historical). */
export function Approved() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [fGroup, setFGroup] = useState("");
  const [fUser, setFUser] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try { setRows(await api.approvedList()); }
    catch (e) { setErr(e.message); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => Array.from(new Set((rows || []).map((r) => r.group).filter(Boolean))).sort(), [rows]);
  const users = useMemo(() => Array.from(new Set((rows || []).map((r) => r.approvedBy).filter(Boolean))).sort(), [rows]);

  const approvedDay = (r) => {
    if (r.approvedDate) { const d = parseDate(r.approvedDate); if (d) return d; }
    if (r.approvedAt) { const t = new Date(r.approvedAt); if (!isNaN(t)) return new Date(t.getFullYear(), t.getMonth(), t.getDate()); }
    return null;
  };
  const fmtApproved = (r) => {
    if (r.approvedDate) return r.approvedDate;
    if (r.approvedAt) { try { return new Date(r.approvedAt).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }); } catch { return "—"; } }
    return "—";
  };

  const filtered = (rows || []).filter((r) => {
    if (fGroup && r.group !== fGroup) return false;
    if (fUser && r.approvedBy !== fUser) return false;
    if (fFrom || fTo) {
      const dv = approvedDay(r);
      if (!dv) return false;
      if (fFrom) { const [y, mo, d2] = fFrom.split("-").map(Number); if (dv < new Date(y, mo - 1, d2)) return false; }
      if (fTo) { const [y, mo, d2] = fTo.split("-").map(Number); if (dv > new Date(y, mo - 1, d2)) return false; }
    }
    if (q) {
      const s = q.toLowerCase();
      if (![r.mrn, r.orderName, r.clinic, r.payor, r.authNumber, r.approvedBy, r.cpts, r.icds].some((v) => (v || "").toString().toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const clearFilters = () => { setFGroup(""); setFUser(""); setFFrom(""); setFTo(""); setQ(""); };

  const exportExcel = () => {
    const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const cols = [["PID", "mrn"], ["Order", "orderName"], ["Order date", "orderDate"], ["Clinic", "clinic"], ["Group", "group"], ["Payor", "payor"], ["Portal", "portal"], ["CPTs", "cpts"], ["ICDs", "icds"], ["Auth #", "authNumber"], ["Request #", "requestNumber"], ["Approved by", "approvedBy"], ["Processes", "processCount"]];
    const head = cols.map(([l]) => `<th style="background:#15803d;color:#fff;padding:6px 10px;border:1px solid #ccc">${esc(l)}</th>`).join("") + `<th style="background:#15803d;color:#fff;padding:6px 10px;border:1px solid #ccc">Approved date</th>`;
    const body = filtered.map((r) => "<tr>" + cols.map(([, k]) => `<td style="padding:5px 10px;border:1px solid #ddd">${esc(r[k])}</td>`).join("") + `<td style="padding:5px 10px;border:1px solid #ddd">${esc(fmtApproved(r))}</td></tr>`).join("");
    const html = `<html><head><meta charset="utf-8"></head><body><table style="border-collapse:collapse;font-family:Arial;font-size:12px"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `approved-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const openRecord = async (r) => {
    if (!r.recordId) return;
    try { const full = await api.getRecord(r.recordId); setViewing(full); }
    catch { setViewing({ id: r.recordId, mrn: r.mrn }); }
  };

  return (
    <div className="hist-full">
      <div className="filter-bar">
        <div className="search"><Icon.search /><input className="inp" placeholder="Search PID, order, payor, auth…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select className="inp" value={fGroup} onChange={(e) => setFGroup(e.target.value)}>
          <option value="">All groups</option>{groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="inp" value={fUser} onChange={(e) => setFUser(e.target.value)}>
          <option value="">All users</option>{users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <label className="filter-date">From <input className="inp" type="date" value={fFrom} max={fTo || undefined} onChange={(e) => setFFrom(e.target.value)} /></label>
        <label className="filter-date">To <input className="inp" type="date" value={fTo} min={fFrom || undefined} onChange={(e) => setFTo(e.target.value)} /></label>
        <button className="link-btn" onClick={clearFilters}>Clear</button>
        <button className="btn btn-primary btn-sm" onClick={exportExcel} disabled={!filtered.length} style={{ marginLeft: "auto" }}>Export to Excel</button>
      </div>

      <div className="card"><div className="table-wrap grid-scroll">
        <table className="tbl hist-tbl">
          <thead><tr>
            <th>PID</th><th>Order</th><th>Clinic</th><th>Group</th><th>Payor</th><th>CPTs</th><th>ICDs</th>
            <th>Auth #</th><th>Approved by</th><th>Approved on</th><th title="How many processes it moved through to get approved">Processes</th>
          </tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="row-click" onClick={() => openRecord(r)} title="Open full record">
                <td className="mono">{r.mrn}</td>
                <td className="wrap">{r.orderName || "—"}</td>
                <td className="cell-sub">{r.clinic || "—"}</td>
                <td className="cell-sub">{r.group || "—"}</td>
                <td className="cell-sub wrap">{r.payor || "—"}</td>
                <td className="mono">{r.cpts || "—"}</td>         
                <td className="mono">{r.icds || "—"}</td>
                <td className="mono">{r.authNumber || "—"}</td>
                <td className="cell-sub">{r.approvedBy || "—"}</td>
                <td className="cell-sub" style={{ whiteSpace: "nowrap" }}>{fmtApproved(r)}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>{r.processCount != null ? r.processCount : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows === null && <div className="empty"><Spinner /></div>}
        {err && <div className="err-text" style={{ padding: 18 }}>{err}</div>}
        {rows && filtered.length === 0 && !err && <Empty icon={<Icon.shield width="40" height="40" />} title="No approved records" text="Approved records will appear here." />}
      </div></div>

      {viewing && <RecordModal record={viewing} onClose={() => setViewing(null)} canEdit={false} />}
    </div>
  );
}